import { eq, object, peek } from '@art-suite/art-core-ts';
import { ApiOrgProduct, getThdIsProductInStock, ScanPartApprovalStatus } from '@kaiizen/kai-pro-api';
import {
  getResolvedContractorId,
  isLineItemScoped,
  isTaskPresent,
  isTaskShownOnScopeReport,
  ScanRootOptimized,
  ScanTaskOptimized,
  taskHasScopeChanges,
} from '@kaiizen/kai-scan-template-system';

export type TaskIdSlices = {
  // all below only include scoped tasks
  changedScopedTaskIds: string[];
  deferredTaskIds: string[];
  editRequestTaskIds: string[];
  needsReworkTaskIds: string[];
  outOfStockTaskIds: string[];
  scopedTaskIds: string[];
  presentTaskIds: string[];
  taskIdsByAssignedContractorId: Record<string, string[]>;
  /**
   * Scoped tasks keyed by each modifier tag their line items carry (KAIDEV-4206).
   *
   * Keyed by tag rather than by a fixed pair of fields because an org mints a tag for every
   * Display modifier it creates, so this code cannot know the key set at build time.
   * `deferredTaskIds` and `tenantResponsibilityTaskIds` are now views onto this map, which is why
   * they still agree with it exactly.
   */
  taskIdsByModifierTag: Record<string, string[]>;
  taskIdsByTrade: Record<string, string[]>;
  taskIdToGroupId: Record<string, string>;
  tenantResponsibilityTaskIds: string[];
  unassignedTaskIds: string[];
  unassignedTaskIdsByTrade: Record<string, string[]>;
};

/**
 * Every tag set on any of a task's line items.
 *
 * Deliberately the same reach as the per-tag `some` check it replaced — every option and
 * every line item, `notApplicable` included — so the counts this feeds are unchanged for the two
 * tags that already had them.
 *
 * The modifier *tags* rendered on a row use a narrower reach (`getTaskModifiers`, which skips
 * `notApplicable` at both levels), so a task can be counted by a filter while its row shows no
 * tag.
 *
 * That disagreement is not new. The full-scope artifact already showed " (Deferred Task)" from
 * `isDeferredLineItem`, which only exists for rows passing `isOptionScoped`/`isLineItemScoped` -
 * narrower than this count, which takes any tag on any line item of a task shown on the report.
 * A task whose only deferred line item is `notApplicable` or zero-cost was already counted in the
 * nav while its artifact row said nothing, with both on screen at once.
 *
 * What KAIDEV-4206 changes is its reach: from the two reserved modifiers in one artifact view to
 * every org-minted modifier, and onto the scope editor's task rows as well. So KAIDEV-4360 is a
 * pre-existing defect this change widens - neither inherited untouched nor introduced here.
 * Reconciling the two predicates moves long-standing counts, which is why it is its own ticket
 * (review rounds 1 and 2).
 */
const getTaskLineItemTags = (task: ScanTaskOptimized): Set<string> => {
  const tags = new Set<string>();
  for (const option of task.options ?? []) {
    for (const lineItem of option.lineItems ?? []) {
      for (const [tag, isSet] of Object.entries(lineItem.tags ?? {})) {
        if (isSet === true) tags.add(tag);
      }
    }
  }
  return tags;
};

export const getTaskIdSlices = ({ scanRoot, baselineScanRoot, approvalStatusByTaskId, orgProductsById, thdOrgId, previousTaskIdSlices }: {
  scanRoot?: ScanRootOptimized;
  baselineScanRoot?: ScanRootOptimized;
  approvalStatusByTaskId?: Record<string, ScanPartApprovalStatus>;
  orgProductsById?: Record<string, ApiOrgProduct>;
  thdOrgId?: string;
  previousTaskIdSlices?: TaskIdSlices;
}): TaskIdSlices => {
  const scopedTaskIds: string[] = [];
  const presentTaskIds: string[] = [];
  const changedScopedTaskIds: string[] = [];
  // Null prototype: see the accumulation below. Not just `__proto__` - `constructor`, `toString`
  // and every other Object.prototype member is a tag an admin could type, and each one throws.
  const taskIdsByModifierTag: Record<string, string[]> = Object.create(null);
  const unassignedTaskIds: string[] = [];
  const outOfStockTaskIds: string[] = [];
  const editRequestTaskIds: string[] = [];
  const needsReworkTaskIds: string[] = [];
  const taskIdsByTrade: Record<string, string[]> = {};
  const taskIdsByAssignedContractorId: Record<string, string[]> = {};
  const taskIdToGroupId: Record<string, string> = {};
  const unassignedTaskIdsByTrade: Record<string, string[]> = {};

  const checkOutOfStock = orgProductsById && thdOrgId;

  if (scanRoot?.groups) {
    const defaultContractorsByTrade = scanRoot.defaultContractorsByTrade;
    const baselineDefaultContractorsByTrade = baselineScanRoot?.defaultContractorsByTrade;
    const baselineGroupsById = baselineScanRoot?.groupsById ?? {};
    for (const group of scanRoot.groups) {
      if (!group.tasks) continue;
      const baselineGroup = baselineGroupsById[group.id];
      for (const task of group.tasks) {
        taskIdToGroupId[task.id] = group.id;
        if (isTaskPresent(task)) presentTaskIds.push(task.id);
        if (isTaskShownOnScopeReport(task)) {
          scopedTaskIds.push(task.id);
          const assignedContractorId = getResolvedContractorId(task.template.trade, task.assignedContractorId, defaultContractorsByTrade);
          if (assignedContractorId) {
            (taskIdsByAssignedContractorId[assignedContractorId] ??= []).push(task.id);
          }

          if (task.template.trade) {
            (taskIdsByTrade[task.template.trade] ??= []).push(task.id);
          }

          if (
            taskHasScopeChanges(
              task,
              baselineGroup?.tasksById?.[task.id],
              defaultContractorsByTrade,
              baselineDefaultContractorsByTrade,
            )
          ) {
            changedScopedTaskIds.push(task.id);
          }

          for (const tag of getTaskLineItemTags(task)) {
            /* `taskIdsByModifierTag` has a null prototype: a tag is free text an org admin types
               in Org Settings, so `__proto__` is a reachable key, and on a plain object
               `obj['__proto__'] ??= []` finds Object.prototype, skips the assignment, and throws
               on `.push` - taking down every consumer of the scan-editing context. The old code
               only ever read two literal keys, so this is new exposure (review round 1). */
            (taskIdsByModifierTag[tag] ??= []).push(task.id);
          }
          if (!assignedContractorId) {
            const trade = task.template.trade;
            const defaultContractorByTrade = (trade ? defaultContractorsByTrade?.[trade] : undefined)
              ?? defaultContractorsByTrade?.general;
            if (!defaultContractorByTrade) {
              unassignedTaskIds.push(task.id);
              if (trade) {
                (unassignedTaskIdsByTrade[trade] ??= []).push(task.id);
              }
            }
          }

          const progress = peek(task.progressUpdates);
          if (progress?.status === 'needsRework') needsReworkTaskIds.push(task.id);
          if (approvalStatusByTaskId?.[task.id] === 'editRequested') editRequestTaskIds.push(task.id);

          if (checkOutOfStock) {
            for (const lineItem of task.options?.flatMap(option => option.lineItems ?? []) ?? []) {
              if (!isLineItemScoped(lineItem)) continue;
              const product = orgProductsById[lineItem.skuId || ''];
              if (product?.productSupplierOrgId !== thdOrgId) continue;
              if (!getThdIsProductInStock(product)) outOfStockTaskIds.push(task.id);
            }
          }
        }
      }
    }
  }

  // The two reserved modifiers' tags are their keys, and plenty of code still reads these by name
  // (`changeOrderLib`, the reports, the scope-editor filters). They are views onto the map now, so
  // a reserved modifier and an org-minted one cannot drift apart.
  const result: TaskIdSlices = {
    changedScopedTaskIds,
    deferredTaskIds: taskIdsByModifierTag.deferred ?? [],
    editRequestTaskIds,
    needsReworkTaskIds,
    outOfStockTaskIds: checkOutOfStock ? outOfStockTaskIds : (previousTaskIdSlices?.outOfStockTaskIds ?? outOfStockTaskIds),
    scopedTaskIds,
    presentTaskIds,
    taskIdsByAssignedContractorId,
    taskIdsByModifierTag,
    taskIdsByTrade,
    tenantResponsibilityTaskIds: taskIdsByModifierTag.tenantResponsibility ?? [],
    unassignedTaskIds,
    unassignedTaskIdsByTrade,
    taskIdToGroupId,
  };

  if (previousTaskIdSlices) {
    return object(result, (taskIds: string[], key: keyof TaskIdSlices) => {
      const oldTasksIds = previousTaskIdSlices[key];
      return eq(taskIds, oldTasksIds) ? oldTasksIds : taskIds;
    });
  }
  return result;
};
